// Lib
import { hot } from 'react-hot-loader/root';
import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import DocumentTitle from 'react-document-title';
import { useSelector } from 'react-redux';
import { values } from 'lodash';
import classNames from 'classnames';

// Utils
import { isDesktopMode, isPlatformNativeApp } from '../platform/utils/platformDetailsUtils';
import { getPlatformDetailsSelector } from '../platform/platformSelector';
import { getIsSidebarPrototypeEnabled } from './sidebar/sidebarSelector';

// Components
import CollaborationManager from '../utils/collaboration/CollaborationManager';

import WorkspaceDragLayer from './dnd/WorkspaceDragLayer';
import WorkspaceErrorNoticeManager from '../components/error/WorkspaceErrorNoticeManager';

import WorkspaceKeyboardShortcutHandler from './shortcuts/WorkspaceKeyboardShortcutHandler';
import DragModifierKeysListener from '../utils/dnd/modifierKeys/DragModifierKeysListener';
import FileUploadWindowCloseBlocker from './header/currentBoardHeader/fileUploadProgress/FileUploadWindowCloseBlocker';
import UnsavedCommentWindowCloseBlocker from '../element/comment/UnsavedCommentWindowCloseBlocker';
import AppMultipleRefreshHandler from '../offline/AppMultipleRefreshHandler';

import AppStoreMetaLink from '../mobile/AppStoreMetaLink';
import WorkspaceWelcomeMessageModal from './welcomeMessage/WorkspaceWelcomeMessageModal';
import ExportingZipContainer from './export/ExportingZipContainer';
import WorkspaceModalManager from '../components/modal/WorkspaceModalManager';
import DebugToolbarContainer from '../debug/DebugToolbarContainer';
import ConnectedElementDragLayer from '../canvas/dnd/ConnectedElementDragLayer';
import PageLoader from '../components/loaders/PageLoader';
import WorkspaceDndProvider from '../utils/dnd/WorkspaceDndProvider';
import { getGroupedItemType, GROUPED_DND_TYPES } from '../utils/dnd/dragAndDropUtils';
import ElementFilterFocusedManager from '../element/elementFilter/ElementFilterFocusedManager';
import WorkspaceSwitch from './WorkspaceSwitch';
import FavouriteBoardsSidebar from '../sidebar/Sidebar';
import WorkspaceToasts from './WorkspaceToasts';

/* ----
 * WorkspaceContainer
 * This component the middle level of the workspace stack
 * it's responsible for interaction:
 *    - drag & drop
 *    - keyboard shortcuts
 */
const WorkspaceContainer = (props) => {
    const { currentBoardId, currentBoard, pageTitle, location, showLoader, permissions, currentUser } = props;

    const workspaceWrapper = useRef();
    const platformDetails = useSelector(getPlatformDetailsSelector);

    const isSidebarProtypeEnabled = useSelector(getIsSidebarPrototypeEnabled);

    const showWorkspaceSidebar = isSidebarProtypeEnabled && isDesktopMode(platformDetails);

    const setIsDragging = useCallback((isDragging, itemType) => {
        if (workspaceWrapper && workspaceWrapper.current) {
            if (isDragging) {
                const groupedItemType = getGroupedItemType(itemType);

                groupedItemType && workspaceWrapper.current.classList.add(`workspace-dragging-${groupedItemType}`);
            } else {
                workspaceWrapper.current.classList.remove(
                    ...values(GROUPED_DND_TYPES).map((type) => `workspace-dragging-${type}`),
                );
            }
        }
    }, []);

    if (showLoader) {
        return isPlatformNativeApp(platformDetails) ? <PageLoader /> : null;
    }

    return (
        <CollaborationManager currentBoardId={currentBoardId}>
            <DocumentTitle title={pageTitle}>
                <WorkspaceDndProvider>
                    <div
                        className={classNames('WorkspaceWrapper', { 'show-sidebar': showWorkspaceSidebar })}
                        ref={workspaceWrapper}
                    >
                        {showWorkspaceSidebar && <FavouriteBoardsSidebar {...props} />}
                        <WorkspaceSwitch {...props} />
                        <ElementFilterFocusedManager />
                        <AppStoreMetaLink currentBoardId={currentBoardId} />
                        <WorkspaceModalManager currentBoardId={currentBoardId} />
                        <WorkspaceErrorNoticeManager currentBoardId={currentBoardId} location={location} />
                        <WorkspaceWelcomeMessageModal currentBoardId={currentBoardId} currentBoard={currentBoard} />
                        <WorkspaceToasts />
                        <ExportingZipContainer {...props} />
                        <WorkspaceDragLayer
                            setIsDragging={setIsDragging}
                            currentBoardId={currentBoardId}
                            permissions={permissions}
                        />
                        <ConnectedElementDragLayer currentBoardId={currentBoardId} />

                        <DragModifierKeysListener />
                        <WorkspaceKeyboardShortcutHandler currentUser={currentUser} />
                        <FileUploadWindowCloseBlocker />
                        <UnsavedCommentWindowCloseBlocker />
                        <AppMultipleRefreshHandler />
                        <DebugToolbarContainer {...props} />
                    </div>

                    {/* Unfortunately we need to use a separate mount point for mentions to avoid un-mounts */}
                    <div id="mention-mount-point" />
                </WorkspaceDndProvider>
            </DocumentTitle>
        </CollaborationManager>
    );
};

WorkspaceContainer.propTypes = {
    currentUser: PropTypes.object,
    currentBoardId: PropTypes.string,
    currentBoard: PropTypes.object,
    permissions: PropTypes.number,
    showLoader: PropTypes.bool,
    pageTitle: PropTypes.string,
    location: PropTypes.object,
    isGuest: PropTypes.bool,
};

export default hot(WorkspaceContainer);
